import React, { useEffect, useRef, useState } from 'react'
import { setLineChartStoreToCurrentTime } from '../utils'
import { LineChartStore } from '../store'
import { EmsBatteryMode, EmsSgReadyMode } from 'src/Libs/dynamic-power-prices/types'
import { AI360ChartData } from '../types'

interface Props {
  chartData: AI360ChartData[]
  from: Date
}

/**
 * set the tooltip at current time
 * re-render on mount , date change, and container height change.
 * container height is observed to make sure the tooltip is correctly positioned
 * even when the chart size changes due to screen rotation or other factors.
 **/
export const useAI360InitializeCurrentDateForTooltip = ({ chartData, from }: Props): React.RefObject<HTMLDivElement> => {
  const containerRef = useRef<HTMLDivElement>(null)

  const [height, setHeight] = useState(0)

  /* observe container height. */
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver(entries => setHeight(entries[0].contentRect.height))
    observer.observe(el)
    return () => observer.disconnect()
  }, [containerRef])

  /*
   * Recharts reads Tooltip's `defaultIndex` synchronously in `componentDidMount`, once, and never again.
   * If we only set it from a `useEffect` below, that effect (passive) always runs after the chart has
   * already mounted, so the cursor line never shows up until the user moves the mouse. Setting it here,
   * during render, guarantees LineChartStore has the right index before the chart's own mount runs.
   */
  const hasSetInitialIndexRef = useRef(false)
  if (!hasSetInitialIndexRef.current) {
    hasSetInitialIndexRef.current = setLineChartStoreToCurrentTime(chartData)
  }

  useEffect(() => {
    setLineChartStoreToCurrentTime(chartData)

    return () => {
      LineChartStore.set({
        time: null,
        powerPrice: null,
        solarProduction: null,
        solarForecast: null,
        emsBatteryMode: EmsBatteryMode.NormalOperation,
        emsSgReadyMode: EmsSgReadyMode.Normal,
        defaultDataIndex: null,
      })
    }
  }, [from, chartData, height])

  return containerRef
}
